import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { ApiConstant, AppConstant, KeyConstant, LangConstant } from "const";
import { ApiUtils, HumpUtils, StorageUtils, SystemUtils } from "utils";

const DEFAULT_FILTERS = {
  search: "",
  page: 1,
  channelSize: "all",
  groupType: "channel", // "channel" | "group"
};

const MAX_FETCH_LIMIT = 10000;

export default function useChannelList() {
  const { t: getLabel } = useTranslation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const channelListQuery = useQuery({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [ApiConstant.REACT_QUERY_KEYS.getChannelList, filters.groupType, filters.search],
    queryFn: async () => {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);

      const groupTypeValue = filters.groupType === "group" ? 1 : 2;
      const filterCondition = { group_type: groupTypeValue };

      if (filters.search) filterCondition.name = filters.search;

      const params = HumpUtils.decamelizeKeys({
        branchId,
        paging: 0,
        limit: MAX_FETCH_LIMIT,
        offset: 0,
        asc: 1,
        groupType: groupTypeValue,
        filter: JSON.stringify(filterCondition),
      });

      const response = await ApiUtils.createApiWithToken(false).get(ApiConstant.GET_CHANNEL_LIST, params);

      if (response.data?.status === ApiConstant.STT_OK) {
        return HumpUtils.camelizeKeys(response.data);
      }

      throw response.data;
    },
    onError: error => {
      console.error(error);
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  const responseData = channelListQuery.data?.data || {};
  const allChannels = useMemo(() => {
    let items = (responseData.data || []).map(item => {
      let groupName = item.name || "";
      try {
        const parsed = JSON.parse(item.name || "{}");
        if (parsed.name && typeof parsed.name === "string" && parsed.name.trim() !== "") {
          groupName = parsed.name.trim();
        } else if (parsed.ids) {
          groupName = item.groupType === 2 ? (getLabel("TXT_GROUP_TYPE_CHANNEL") || "Kênh") : (getLabel("TXT_GROUP_TYPE_GROUP") || "Nhóm");
        }
      } catch (e) {
        groupName = item.name || (item.groupType === 2 ? (getLabel("TXT_GROUP_TYPE_CHANNEL") || "Kênh") : (getLabel("TXT_GROUP_TYPE_GROUP") || "Nhóm"));
      }
      return { ...item, groupName };
    });

    // Client-side filter by member count (size)
    const sizeFilter = AppConstant.CHANNEL_SIZE_FILTER[filters.channelSize];
    if (sizeFilter) {
      if (sizeFilter.min !== null) {
        items = items.filter(item => (item.memberCount || 0) >= sizeFilter.min);
      }
      if (sizeFilter.max !== null) {
        items = items.filter(item => (item.memberCount || 0) <= sizeFilter.max);
      }
    }

    // Sort by member count descending
    items.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));
    return items;
  }, [responseData.data, filters.channelSize]);

  // Client-side pagination
  const totalChannels = allChannels.length;
  const totalPages = Math.ceil(totalChannels / AppConstant.PAGE_SIZE) || 1;
  // Auto-adjust page if current page exceeds total pages (e.g. after batch delete on last page)
  const safePage = Math.min(filters.page, totalPages);
  const channels = allChannels.slice((safePage - 1) * AppConstant.PAGE_SIZE, safePage * AppConstant.PAGE_SIZE);

  const handleSearchChange = useCallback(
    debounce(value => {
      setFilters(prev => ({ ...prev, search: value, page: 1 }));
    }, AppConstant.INPUT_DEBOUNCE_TIME),
    [],
  );

  const handlePageChange = (_, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleChannelSizeChange = value => {
    setFilters(prev => ({ ...prev, channelSize: value, page: 1 }));
  };

  const handleGroupTypeChange = value => {
    setFilters(prev => ({ ...prev, groupType: value, page: 1, search: "", channelSize: "all" }));
  };

  return {
    channels,
    totalChannels,
    page: safePage,
    totalPages,
    isLoading: channelListQuery.isLoading,
    isFetching: channelListQuery.isFetching,
    refetch: channelListQuery.refetch,
    groupType: filters.groupType,
    channelSize: filters.channelSize,
    search: filters.search,
    handleSearchChange,
    handlePageChange,
    handleChannelSizeChange,
    handleGroupTypeChange,
  };
}
